/* contact-map.js — load the Google Maps embed only when the visitor asks for it.
 *
 * This is a Dutch B2B store, so an iframe that fires on page load would hand Google a request
 * (and cookies) from every visitor before anyone consented to anything. The Liquid therefore
 * renders a finished placeholder — pin, address, a load button, a one-line note — and this script
 * does exactly one thing: swap that placeholder for the real iframe on click.
 *
 * Everything here is an enhancement on top of a finished render. If the script never loads, fails,
 * or JS is off, the placeholder stays and the "Open in Maps" link beside it still works — the same
 * contract about-stats.js and about-timeline.js follow. The theme editor gets the placeholder too,
 * so a merchant dragging sections around never waits on a third-party frame.
 */

class ContactMap extends HTMLElement {
  connectedCallback() {
    this.placeholder = this.querySelector('[data-map-placeholder]');
    this.trigger = this.querySelector('[data-map-load]');
    if (!this.placeholder || !this.trigger) return;

    // No embed URL means the merchant left the address blank — the button would load nothing, so
    // it should not look interactive at all.
    if (!this.dataset.embedUrl) {
      this.trigger.hidden = true;
      return;
    }

    this.onClick = this.load.bind(this);
    this.trigger.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    // Guard the thing being dereferenced, not the handler. After a load the placeholder and its
    // button are gone, so a detach/re-attach (theme-editor reorder, any script moving the node)
    // left `onClick` truthy and `trigger` null, and this threw on the next detach.
    if (this.trigger && this.onClick) this.trigger.removeEventListener('click', this.onClick);
  }

  load() {
    if (this.loaded) return;
    this.loaded = true;

    const frame = document.createElement('iframe');
    frame.src = this.dataset.embedUrl;
    frame.title = this.dataset.frameTitle || 'Map';
    frame.loading = 'lazy';
    frame.referrerPolicy = 'no-referrer-when-downgrade';
    frame.allowFullscreen = true;
    frame.className = 'contact-form__map-frame';

    this.placeholder.remove();
    this.appendChild(frame);
    // the click moved focus into a control that no longer exists — put it on the frame so a
    // keyboard visitor is not dropped back at the top of the document
    frame.focus({ preventScroll: true });
  }
}

if (!customElements.get('contact-map')) customElements.define('contact-map', ContactMap);
